/**
 * Shared Recludo Fund-Flow Report Page
 *
 * Public, read-only page rendered via signed share token. No auth required —
 * token validation lives in getSharedRecludoReport. Visual + structural
 * conventions ported from SharedBrokerReportPage (dark theme, VALUTTEN
 * header, @media print blocker, SectionHeading + MetricCard primitives).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { Loader2, AlertCircle, ArrowLeft, ShieldCheck, ShieldAlert } from 'lucide-react';

type ReconciliationStatus = 'matched' | 'mismatch' | 'no_data';

interface Bucket {
  recipient: string;
  aggregator: string;
  line: string;
  incCents: number;
  exCents: number;
  gstTreatment: 'inclusive' | 'free';
  rowCount: number;
}

interface RecipientTotal {
  recipient: string;
  line: string;
  incCents: number;
  exCents: number;
}

interface ReconciliationLine {
  stream: string;
  rctiCents: number;
  commCents: number;
  diffCents: number;
}

interface Snapshot {
  schemaVersion: number;
  companyId: string;
  companyName: string;
  periodMonth: string;
  generatedAt: string;
  reconciliationStatus: ReconciliationStatus;
  reconciliationDiffCents: number;
  buckets: Bucket[];
  totalsByRecipient: RecipientTotal[];
  reconciliation: ReconciliationLine[];
}

const formatCurrency = (cents: number): string =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);

const formatPeriod = (periodMonth: string): string => {
  const [y, m] = periodMonth.split('-');
  const date = new Date(Number(y), Number(m) - 1);
  return date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
};

const SharedRecludoFundFlowPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetch = async () => {
      if (!token) {
        setError('No share token provided');
        setIsLoading(false);
        return;
      }
      try {
        const fn = httpsCallable<{ token: string }, Snapshot>(functions, 'getSharedRecludoReport');
        const response = await fn({ token });
        setSnapshot(response.data);
      } catch (err: any) {
        console.error('Failed to load shared Recludo report:', err);
        if (err.code === 'functions/permission-denied') {
          setError('This share link is invalid or has expired');
        } else if (err.code === 'functions/not-found') {
          setError('Report data not found');
        } else {
          setError(err.message || 'Failed to load report');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, [token]);

  const grouped = useMemo(() => {
    if (!snapshot) return {} as Record<string, Bucket[]>;
    const out: Record<string, Bucket[]> = {};
    for (const b of snapshot.buckets) {
      out[b.recipient] ||= [];
      out[b.recipient].push(b);
    }
    return out;
  }, [snapshot]);

  const recipientTotals = useMemo(() => {
    if (!snapshot) return {} as Record<string, { incCents: number; exCents: number }>;
    const out: Record<string, { incCents: number; exCents: number }> = {};
    for (const t of snapshot.totalsByRecipient) {
      out[t.recipient] ||= { incCents: 0, exCents: 0 };
      out[t.recipient].incCents += t.incCents;
      out[t.recipient].exCents += t.exCents;
    }
    return out;
  }, [snapshot]);

  const totals = useMemo(() => {
    if (!snapshot) return { incCents: 0, exCents: 0 };
    return Object.values(recipientTotals).reduce(
      (acc, r) => ({ incCents: acc.incCents + r.incCents, exCents: acc.exCents + r.exCents }),
      { incCents: 0, exCents: 0 },
    );
  }, [snapshot, recipientTotals]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-lg">Loading fund-flow report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-red-500/30 text-center">
          <div className="p-4 bg-red-500/20 rounded-full w-fit mx-auto mb-4">
            <AlertCircle className="h-10 w-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Report</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-semibold transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (!snapshot) return null;

  const recipientOrder = Object.keys(grouped).sort((a, b) => {
    const priority = ['Kat', 'Di', 'RVF'];
    const ai = priority.indexOf(a);
    const bi = priority.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  });

  const reconciliationOk = snapshot.reconciliationStatus === 'matched';
  const reconciliationNoData = snapshot.reconciliationStatus === 'no_data';

  return (
    <>
      <style>{`
        @media print {
          html, body { display: none !important; }
        }
      `}</style>

      <div className="min-h-screen bg-[#0f1117] text-white">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-8 space-y-6">
          {/* Header */}
          <div className="relative pb-5 border-b-2 border-emerald-500">
            <div className="flex justify-between items-start">
              <img
                src="/valutten-logo.jpg"
                alt="VALUTTEN"
                className="h-12"
                style={{ mixBlendMode: 'lighten' }}
              />
              <div className="text-right">
                <span className="inline-block bg-white/5 border border-white/10 rounded-md px-2.5 py-1 text-[11px] uppercase tracking-widest text-gray-400 mb-1">
                  Confidential Report
                </span>
                <div className="text-sm text-gray-500">
                  {new Date(snapshot.generatedAt).toLocaleDateString('en-AU', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mt-4">
              Monthly Fund-Flow — {formatPeriod(snapshot.periodMonth)}
            </h1>
            <p className="text-sm text-gray-400 mt-1">Prepared by {snapshot.companyName}</p>
          </div>

          {/* Totals strip */}
          <section>
            <SectionHeading title="Summary" period={`Period: ${formatPeriod(snapshot.periodMonth)}`} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard
                label="Total Inc-GST"
                value={formatCurrency(totals.incCents)}
                sub="All recipients"
                color="#10b981"
                hero
              />
              <MetricCard
                label="Total Ex-GST"
                value={formatCurrency(totals.exCents)}
                sub="Ex 1/11 GST"
                color="#3b82f6"
              />
              <MetricCard
                label="Recipients"
                value={String(recipientOrder.length)}
                sub="Allocated buckets"
                color="#8b5cf6"
              />
              <MetricCard
                label="Source rows"
                value={String(snapshot.buckets.reduce((s, b) => s + b.rowCount, 0))}
                sub="Grouped raw commissions"
                color="#f59e0b"
              />
            </div>
          </section>

          {/* Per-recipient waterfall */}
          {recipientOrder.map((recipient) => {
            const buckets = grouped[recipient];
            const total = recipientTotals[recipient];
            return (
              <section key={recipient}>
                <SectionHeading
                  title={recipient}
                  period={`Inc ${formatCurrency(total.incCents)} · Ex ${formatCurrency(total.exCents)}`}
                />
                <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">
                          Aggregator
                        </th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">
                          Line
                        </th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">
                          GST
                        </th>
                        <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">
                          Inc-GST
                        </th>
                        <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">
                          Ex-GST
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {buckets.map((b, i) => (
                        <tr
                          key={`${b.aggregator}-${b.line}-${b.gstTreatment}`}
                          className={i % 2 === 1 ? 'bg-white/[0.015]' : ''}
                        >
                          <td className="px-4 py-2.5 text-white font-medium">{b.aggregator}</td>
                          <td className="px-4 py-2.5 text-gray-300">{b.line}</td>
                          <td className="px-4 py-2.5 text-gray-400 text-xs uppercase">
                            {b.gstTreatment === 'free' ? 'Free' : 'Inclusive'}
                          </td>
                          <td
                            className={`px-4 py-2.5 text-right font-mono ${
                              b.incCents < 0 ? 'text-red-300' : 'text-white'
                            }`}
                          >
                            {formatCurrency(b.incCents)}
                          </td>
                          <td
                            className={`px-4 py-2.5 text-right font-mono ${
                              b.exCents < 0 ? 'text-red-300' : 'text-gray-300'
                            }`}
                          >
                            {formatCurrency(b.exCents)}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-white/[0.04] border-t border-white/[0.08] font-semibold">
                        <td className="px-4 py-2.5" colSpan={3}>
                          Total {recipient}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-white">
                          {formatCurrency(total.incCents)}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-white">
                          {formatCurrency(total.exCents)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}

          {/* Reconciliation */}
          <section>
            <SectionHeading
              title="Reconciliation"
              period={
                reconciliationNoData
                  ? 'No RCTI data found for period'
                  : reconciliationOk
                    ? 'Matched — RCTI = Comm'
                    : `Mismatch — ${formatCurrency(snapshot.reconciliationDiffCents)} difference`
              }
            />
            <div
              className={`flex items-start gap-3 rounded-xl border p-4 mb-3 ${
                reconciliationOk
                  ? 'bg-emerald-500/10 border-emerald-500/30'
                  : reconciliationNoData
                    ? 'bg-amber-500/10 border-amber-500/30'
                    : 'bg-red-500/10 border-red-500/30'
              }`}
            >
              {reconciliationOk ? (
                <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              ) : (
                <ShieldAlert
                  className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                    reconciliationNoData ? 'text-amber-400' : 'text-red-400'
                  }`}
                />
              )}
              <div className="text-sm">
                {reconciliationOk && (
                  <>
                    <p className="font-semibold text-emerald-300">All streams reconcile to the cent.</p>
                    <p className="text-gray-400 mt-1">
                      RCTI totals from upload_runs match the allocated commission totals exactly for
                      every aggregator stream.
                    </p>
                  </>
                )}
                {reconciliationNoData && (
                  <>
                    <p className="font-semibold text-amber-300">No RCTI data for this period.</p>
                    <p className="text-gray-400 mt-1">
                      Allocated commission totals are shown; RCTI cross-check could not be performed
                      because no completed upload_runs were found for {formatPeriod(snapshot.periodMonth)}.
                    </p>
                  </>
                )}
                {!reconciliationOk && !reconciliationNoData && (
                  <>
                    <p className="font-semibold text-red-300">RCTI / Comm mismatch detected.</p>
                    <p className="text-gray-400 mt-1">
                      Review the per-stream breakdown below. A non-zero diff usually indicates a
                      pipeline gap (missing upload_runs row) or an aggregator file that arrived in a
                      different reporting month than expected.
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">
                      Stream
                    </th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">
                      RCTI total
                    </th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">
                      Commission total
                    </th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">
                      Diff
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.reconciliation.map((r, i) => (
                    <tr key={r.stream} className={i % 2 === 1 ? 'bg-white/[0.015]' : ''}>
                      <td className="px-4 py-2.5 text-white font-medium">{r.stream}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-300">
                        {formatCurrency(r.rctiCents)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-300">
                        {formatCurrency(r.commCents)}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right font-mono ${
                          r.diffCents === 0 ? 'text-emerald-300' : 'text-red-300'
                        }`}
                      >
                        {formatCurrency(r.diffCents)}
                      </td>
                    </tr>
                  ))}
                  {snapshot.reconciliation.length === 0 && (
                    <tr>
                      <td className="px-4 py-3 text-center text-gray-500 text-sm" colSpan={4}>
                        No reconciliation data available for this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Footer */}
          <footer className="text-center py-6 text-gray-500 text-xs border-t border-white/[0.06] space-y-1">
            <p>This is a shared, read-only fund-flow report.</p>
            <p>
              Powered by <span className="text-emerald-500">VALUTTEN</span>
            </p>
            <p className="text-gray-600">
              Values derived from RCTI / commission feeds. Ex-GST extracted at 1/11. Snapshot is
              immutable — re-loads return the same numbers.
            </p>
          </footer>
        </div>
      </div>
    </>
  );
};

const SectionHeading: React.FC<{ title: string; period?: string }> = ({ title, period }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <span className="text-[13px] font-semibold uppercase tracking-[2px] text-gray-500">{title}</span>
    {period && (
      <span className="text-xs text-gray-400 bg-white/[0.04] rounded px-2 py-0.5">{period}</span>
    )}
    <div className="flex-1 h-px bg-white/[0.06]" />
  </div>
);

const MetricCard: React.FC<{
  label: string;
  value: string;
  sub: string;
  color: string;
  hero?: boolean;
}> = ({ label, value, sub, color, hero }) => (
  <div className="relative bg-gray-800/50 border border-white/[0.06] rounded-xl px-5 py-5 overflow-hidden">
    <div
      className="absolute top-0 left-0 right-0 h-[3px] rounded-t-xl"
      style={{ background: color }}
    />
    <div
      className="absolute -top-7 -right-7 w-28 h-28 rounded-full blur-[50px] opacity-[0.12] pointer-events-none"
      style={{ background: color }}
    />
    <div className="text-[11px] font-medium uppercase tracking-[1.2px] text-gray-500 mb-2">
      {label}
    </div>
    <div
      className={`font-bold leading-none mb-1 ${hero ? 'text-[34px]' : 'text-[28px]'}`}
      style={{ color }}
    >
      {value}
    </div>
    <div className="text-sm text-gray-400">{sub}</div>
  </div>
);

export default SharedRecludoFundFlowPage;
